import { defineStore } from 'pinia'

import { apiClient } from "@/helpers/api.js"
import { handleApiError } from '@/services/errorHandler'
import { useBatchStore } from '@/stores/BatchStore'
import { isSearchInventoryEnabled } from '@/helpers/featureFlags'

// Bumped by every `searchDatabaseMatches` call (and every clear), so a response
// can tell whether it is still the latest one asked for. Module-level rather
// than state: neither is anything a component should render from.
let databaseMatchRequestId = 0
let databaseMatchController = null

// The same latest-wins pair for `searchUserBatches`, kept separate because the
// two searches write different state and must not cancel each other.
let userBatchesRequestId = 0
let userBatchesController = null

const isCanceledRequest = (error) =>
  error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED'

export const useSearchStore = defineStore('SearchStore', {
  state: () => {
    return {
      selectedFilter: '',
      searchState: 'start',

      batchesSearched: [],
      recentlyModifiedBatches: [],
      cdp_search: {
        open: false,
        state: '',
        locationOfSearchCall: ''
      },

      loadingBatches: false,
      loadingMatches: false,
      loadingMoreMatches: false,

      hoveredOverMatchCard: null,

      matchesData: {
        matches: []
      },

      /**
       * Optional `inventory` block on the /search response (CDP-2508) — the
       * user's OWN matching cards, from Typesense. Present only when Typesense
       * is enabled for the user; the key is simply absent otherwise, so this
       * stays an empty list and every consumer can read it unconditionally.
       */
      inventoryData: {
        items: [],
        totalHits: 0,
        page: 1,
        totalPages: 0,
      },

      // Shared seed so every DatabaseMatchField instance for the selected card
      // (regular + pinned copy) stays in sync when a match is removed: they all
      // pre-fill the search input with the removed match title. Keyed by cardId
      // so a stale seed from another card can't leak in.
      databaseMatchSeed: { cardId: null, term: '' },

      triggerOpenSearchResult: false
    }
  },

  actions: {
    updateSearchStateBasedOnDataObject(data) {
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === 'object') {
          Object.entries(data).forEach(([key, value]) => {
            this[key] = value
          })
        } else {
          this[key] = value
        }
      })
    },
    /**
     * Search the dealer's batches. The LATEST call wins.
     *
     * Global search fires one call per query change, plus an unfiltered one
     * when it opens or its filter changes. Applied in arrival order, a slower earlier answer landed
     * after the right one and replaced it — the batch showed up, then vanished,
     * and only reappeared once the term was retyped. So each call aborts the
     * previous one, and a response (or error) that is no longer the latest
     * touches neither the results nor `loadingBatches`.
     */
    async searchUserBatches(options = {}) {
      userBatchesRequestId = userBatchesRequestId + 1
      const requestId = userBatchesRequestId
      const isSuperseded = () => requestId !== userBatchesRequestId

      if (userBatchesController) userBatchesController.abort()
      const controller = new AbortController()
      userBatchesController = controller

      try {
        const { query, kind } = options

        this.loadingBatches = true

        const params = {}

        if (query) {
          params.q = query
        }

        if (kind) {
          params.kind = kind
        }

        const { data } = await apiClient().get(`/batches`, query || kind ? {
          params,
          signal: controller.signal
        } : { signal: controller.signal })

        if (isSuperseded()) return

        userBatchesController = null

        if (!query) {
          this.recentlyModifiedBatches = data.items.sort((a, b) => {
            const dateA = new Date(a.updated_at)
            const dateB = new Date(b.updated_at)
            return dateB - dateA; // Sorting in descending order
          }).slice(0, 5)
        } else {
          this.recentlyModifiedBatches = []
        }
        this.batchesSearched = data.items
        this.loadingBatches = false
      } catch (error) {
        // Aborted or outrun by a newer search: not a failure, and the loading
        // flag is the newer request's to clear.
        if (isSuperseded() || isCanceledRequest(error)) return

        userBatchesController = null
        handleApiError(error, { component: 'SearchStore', action: 'searchUserBatches' })
        this.loadingBatches = false
      }
    },

    /**
     * Fold the response's optional `inventory` block into state.
     *
     * Absent (Typesense off for this user) resets to empty rather than keeping
     * the previous query's hits around — a stale "your inventory" list under a
     * new search would read as a result for THIS query.
     */
    setInventoryData(inventory, page = 1) {
      // Pilot gate (CDP-2508). Dropping the block HERE is what makes every other
      // user's search behave exactly as before: no hits reach the UI, so no
      // inventory section renders and Card Database rows keep "Add Card".
      if (!isSearchInventoryEnabled() || !inventory?.items?.length) {
        if (page === 1) this.inventoryData = { items: [], totalHits: 0, page: 1, totalPages: 0 }
        return
      }

      this.inventoryData = {
        ...inventory,
        // The REQUESTED page, not the echoed one. "Load more" reads
        // `page < totalPages` to decide whether to offer itself, so a response
        // that always reports `page: 1` would leave the button up forever and
        // re-append the same rows on every click.
        page,
        items: page > 1 ? [...this.inventoryData.items, ...inventory.items] : [...inventory.items],
      }
    },

    /**
     * Supersede whatever `/search` is in flight: abort it and make sure its
     * response, if one still lands, is ignored. Every caller writes the SAME
     * `matchesData`, so this is global by design — the newest ask owns the list.
     *
     * @returns {number} the id the caller's own request should carry.
     */
    supersedeDatabaseMatchSearch() {
      databaseMatchRequestId = databaseMatchRequestId + 1
      if (databaseMatchController) databaseMatchController.abort()
      databaseMatchController = null
      return databaseMatchRequestId
    },

    /**
     * Empty the results AND drop the request that would refill them. Assigning
     * `matchesData = { matches: [] }` alone leaves a slow response free to land
     * afterwards and put the old query's results back under an empty box.
     */
    clearDatabaseMatches() {
      this.supersedeDatabaseMatchSearch()
      this.matchesData = { matches: [] }
      this.loadingMatches = false
      this.loadingMoreMatches = false
    },

    /**
     * Run a card-database search. The LATEST call wins.
     *
     * Typing `CG` → `CG-10` sends two overlapping requests, and the shorter,
     * broader query is often the slower one. Applied in arrival order, its
     * results landed after the right ones and replaced them — the list
     * flashed and swapped, and read as if the dash had been dropped. So each
     * call aborts the previous one, and a response (or error) that is no longer
     * the latest touches neither the results nor the loading flags: those
     * belong to the newer request, and flipping `loadingMatches` off from a
     * stale answer would show "No results" while the real one is still coming.
     */
    async searchDatabaseMatches(options = {}, skipLoaderUpdate = false) {
      const { query, page = 1, kind } = options
      const batchStore = useBatchStore()

      const requestId = this.supersedeDatabaseMatchSearch()
      const isSuperseded = () => requestId !== databaseMatchRequestId

      try {
        if (!query && this.selectedFilter === 'Card Database' && batchStore.selectedBatchDetailsViewOption === 'Inspector') {
          // The request this replaced was just aborted, so nothing else will
          // clear its loading flags.
          this.clearDatabaseMatches()
          return
        }

        const controller = new AbortController()
        databaseMatchController = controller

        this.loadingMoreMatches = true

        if (!skipLoaderUpdate) {
          this.loadingMatches = true
        }
       
        const params = {
          page
        }

        if (query) {
          params.q = query
        }

        if (kind) {
          params.kind = kind
        }

        if (options.baseSetIds) {
          params.baseSetIds = options.baseSetIds
        }

        if (options.setIds) {
          params.setIds = options.setIds
        }

        // CDP-2508: `/search` moves to the main Laravel API (`apiClient()`) so
        // the response can carry the optional `inventory` block — but only for
        // the pilot account. Everyone else keeps hitting the batch processor
        // (`apiClient(true)`), i.e. byte-for-byte the previous request, so the
        // endpoint switch can be validated on one account before it ships.
        // Params are identical either way.
        const onLaravelSearch = isSearchInventoryEnabled()

        const { data } = await apiClient(!onLaravelSearch).get(`/search`, query || kind ? {
          params,
          signal: controller.signal
        } : { signal: controller.signal })

        if (isSuperseded()) return

        databaseMatchController = null

        if (page > 1) {
          this.matchesData = { ...data, matches: [...this.matchesData.matches, ...data.matches] }
        } else {
          this.matchesData = { ...data, matches: data.matches ? [...data.matches] : [] }
        }

        this.setInventoryData(data.inventory, page)

        this.loadingMatches = false
        this.loadingMoreMatches = false
      } catch (error) {
        // Aborted or outrun by a newer search: not a failure, and the loading
        // flags are the newer request's to clear.
        if (isSuperseded() || isCanceledRequest(error)) return

        databaseMatchController = null
        this.loadingMatches = false
        this.loadingMoreMatches = false
        handleApiError(error, { component: 'SearchStore', action: 'searchDatabaseMatches' })
      }
    },

  }
})